import React, { useState, useEffect, useRef } from 'react';
import { calculateHalfOctaveBandwidth } from '../hooks/useNotchedWhiteNoise';

export const InteractiveNotchDiagramResponsive: React.FC = () => {
  const animationRef = useRef<number | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  
  // Fewer bars on mobile for clarity
  const barCount = isMobile ? 30 : 70;
  const [barHeights, setBarHeights] = useState<number[]>(() => 
    Array.from({ length: barCount }, () => Math.random())
  );

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Update bar heights when bar count changes
  useEffect(() => {
    setBarHeights(Array.from({ length: barCount }, () => Math.random()));
  }, [barCount]);

  // Static notch at 6kHz with research-based bandwidth
  const notchFrequency = 6000;
  const notchPosition = barCount === 30 ? 23 : 55; // Proportional position
  const notchBandwidth = calculateHalfOctaveBandwidth(notchFrequency); // Research-based 0.5 octave
  const notchWidth = isMobile ? 
    (notchBandwidth / notchFrequency) * 10 : // Scale for mobile visualization
    (notchBandwidth / notchFrequency) * 20; // Scale for desktop visualization

  // Auto-play animation for white noise
  useEffect(() => {
    let frameCount = 0;
    const updateFrequency = isMobile ? 10 : 5; // Slower updates on mobile for performance
    
    const animate = () => {
      frameCount++;
      
      if (frameCount % updateFrequency === 0) {
        setBarHeights(prev => 
          prev.map((oldHeight) => {
            const newHeight = Math.random();
            return oldHeight * 0.7 + newHeight * 0.3;
          })
        );
      }
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animationRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isMobile]);

  // Calculate notch filter curve
  const getNotchAttenuation = (barPosition: number) => {
    const distance = Math.abs(barPosition - notchPosition);
    if (distance > notchWidth) return 1;
    
    const sigma = notchWidth / 3;
    const attenuation = Math.exp(-(distance * distance) / (2 * sigma * sigma));
    
    return 1 - (attenuation * 0.9);
  };

  // Mobile version - simplified
  const MobileVisualization = () => (
    <div className="bg-white rounded-lg p-4">
      <h3 className="text-xl font-bold text-purple-700 mb-2 text-center">
        How It Works
      </h3>
      
      <div className="space-y-4">
        {/* Simplified SVG for mobile */}
        <div className="bg-gray-50 rounded-lg p-3">
          <svg 
            viewBox="0 0 300 150" 
            className="w-full h-auto"
            style={{ maxHeight: '180px' }}
          >
            <defs>
              <linearGradient id="mobileBarGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#a855f7" />
                <stop offset="100%" stopColor="#9333ea" />
              </linearGradient>
              <linearGradient id="mobileNotchGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#ec4899" />
                <stop offset="100%" stopColor="#db2777" />
              </linearGradient>
            </defs>
            
            {/* Frequency spectrum bars */}
            <g transform="translate(10, 20)">
              {barHeights.map((heightFactor, i) => {
                const barWidth = 280 / barCount;
                const x = i * barWidth;
                const barPosition = (i / barCount) * 100;
                
                const attenuation = getNotchAttenuation(barPosition);
                const isInNotch = attenuation < 0.95;
                
                const maxHeight = 100;
                const height = (maxHeight * heightFactor * attenuation) + 5;
                const y = 110 - height;
                
                return (
                  <rect
                    key={i}
                    x={x}
                    y={y}
                    width={barWidth - 1}
                    height={height}
                    fill={isInNotch ? 'url(#mobileNotchGradient)' : 'url(#mobileBarGradient)'}
                    opacity={0.9}
                  />
                );
              })}
            </g>
          </svg>
        </div>

        {/* Frequency labels outside SVG for better readability */}
        <div className="flex justify-between text-xs text-gray-600 px-2">
          <span>20 Hz</span>
          <span className="text-pink-600 font-semibold">▼ 6 kHz (0.5 octave)</span>
          <span>20 kHz</span>
        </div>

        {/* Clear explanation */}
        <div className="text-center space-y-2">
          <p className="text-sm text-gray-700 font-medium">
            White noise with research-optimized frequency removal
          </p>
          <p className="text-xs text-gray-600">
            The <span className="text-pink-600 font-semibold">pink gap</span> shows a {notchBandwidth} Hz notch (0.5 octave bandwidth)
          </p>
        </div>

        {/* Simplified info box */}
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-3 border border-purple-100">
          <p className="text-sm text-purple-700">
            <strong>Why it helps:</strong> Research shows 0.5 octave bandwidth provides optimal 
            therapeutic effect - your brain may gradually "forget" the missing frequency
          </p>
        </div>
      </div>
    </div>
  );

  // Desktop version - detailed
  const DesktopVisualization = () => (
    <div className="bg-white rounded-lg p-6">
      <h3 className="text-2xl font-bold text-purple-700 mb-2 text-center">
        How Notched Sound Therapy Works
      </h3>
      <p className="text-center text-gray-600 mb-6">
        White noise with research-optimized frequency removal (0.5 octave bandwidth)
      </p>
      
      <div className="space-y-4">
        {/* Detailed SVG for desktop */}
        <svg 
          viewBox="0 0 450 280" 
          className="w-full h-auto"
        >
          <rect width="450" height="280" fill="#f8fafc" rx="8" />
          
          <defs>
            <linearGradient id="barGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#e9d5ff" />
              <stop offset="100%" stopColor="#d8b4fe" />
            </linearGradient>
            <linearGradient id="notchGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#fbcfe8" />
              <stop offset="100%" stopColor="#f9a8d4" />
            </linearGradient>
          </defs>
          
          <text x="225" y="30" textAnchor="middle" className="text-base font-medium fill-gray-700">
            Notched White Noise Spectrum
          </text>
          
          <g transform="translate(50, 60)">
            <rect x="0" y="0" width="350" height="120" fill="white" stroke="#e5e7eb" strokeWidth="1" rx="4" />
            
            {barHeights.map((heightFactor, i) => {
              const x = i * 5;
              const barPosition = (i / 70) * 100;
              
              const attenuation = getNotchAttenuation(barPosition);
              const isInNotch = attenuation < 0.95;
              
              const maxHeight = 110;
              const height = (maxHeight * heightFactor * attenuation) + 10;
              const y = 120 - height;
              
              return (
                <rect
                  key={i}
                  x={x}
                  y={y}
                  width="4"
                  height={height}
                  fill={isInNotch ? 'url(#notchGradient)' : 'url(#barGradient)'}
                  opacity={0.8}
                />
              );
            })}
            
            <text x="0" y="140" className="text-xs fill-gray-600">20 Hz</text>
            <text x="87.5" y="140" className="text-xs fill-gray-600" textAnchor="middle">1 kHz</text>
            <text x="175" y="140" className="text-xs fill-pink-600 font-semibold" textAnchor="middle">6 kHz</text>
            <text x="262.5" y="140" className="text-xs fill-gray-600" textAnchor="middle">10 kHz</text>
            <text x="350" y="140" className="text-xs fill-gray-600" textAnchor="end">20 kHz</text>
          </g>
          
          <text x="225" y="235" textAnchor="middle" className="text-sm fill-gray-600">
            Pink shows research-optimized removal ({notchBandwidth} Hz at 6 kHz)
          </text>
        </svg>

        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-4 text-center border border-purple-100">
          <p className="text-purple-700">
            <strong>How it helps:</strong> By removing your tinnitus frequency from white noise, 
            your brain may gradually reduce its focus on that sound. It's like training your brain 
            to "forget" the ringing.
          </p>
        </div>
      </div>
    </div>
  );

  return isMobile ? <MobileVisualization /> : <DesktopVisualization />;
};